// Component: Loader.jsx
// Purpose: DS animated loader — the DeeTrade chart mark, in two same-family
//          forms picked by size:
//            · E-large (md–xl): candlesticks only, in two runtimes off one
//              geometry — DRIVEN (a `progress` number, e.g. PullToRefresh's
//              pull): the candles rise with the pull, then tint teal once armed
//              (progress ≥ 1) as the release cue. LOOP (no progress — modal/
//              panel/auth waits, PTR's refresh phase): a calm "market heartbeat"
//              — the candles breathe in a continuous travelling wave. High
//              contrast (--fg-1) so it reads on either theme. No trend line,
//              dot, arrow or baseline — just the candlesticks.
//            · E-spark (xs–sm): E's trend line + a leading dot travelling
//              continuously (never fully clears — no blink). For buttons/inputs.
//          NOTE: content / route / table / card loading should use <Skeleton>,
//          not this — a skeleton reads faster and keeps the brand gesture rare.
//
//          tone: "muted" (default, --fg-2) | "accent" | "inherit" (button fg).
//
// Usage:
//   <Loader label="Running backtest…" />        // md, E-large, muted
//   <Loader size="sm" tone="inherit" />         // in a button: E-spark, white
//   <Loader size="xs" />                        // in a field: E-spark

import React from "react";
import { useReducedMotion } from "framer-motion";
import { cx } from "./cx";

const LARGE_H = { md: 40, lg: 60, xl: 88 };
const SPARK_H = { xs: 16, sm: 22 };

// tone → class/style. inherit lets the loader take a button's/colored-surface's fg.
const toneProps = (tone) => ({
  cls: tone === "accent" ? "accent" : "",
  style: tone === "inherit" ? { color: "inherit" } : undefined,
});

// large glyph pixel dims: `size` is a large key (md/lg/xl) OR a raw height in px
// (PTR passes a number to hit its ~40–44px-wide box). viewBox aspect 34:24.
const largeDims = (size) => {
  const h = typeof size === "number" ? size : LARGE_H[size] || 40;
  return { h, w: Math.round((h * 34) / 24) };
};

// -- progress → glyph mapping (driven mode) ---------------------------------
// Candles rise ONE BY ONE with the pull: candle i grows across its [start,end]
// band, and the bands barely overlap so each candle is nearly full before the
// next starts lifting — a clear left-to-right stagger as you drag. Smoothstep
// within each band keeps every candle's rise soft (no snap).
const CANDLE_BANDS = [
  [0.0, 0.18],
  [0.16, 0.36],
  [0.34, 0.55],
  [0.52, 0.74],
  [0.7, 0.92],
];
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (t) => t * t * (3 - 2 * t); // smoothstep — kills the linear snap
const bandScale = (p, [a, b]) => smooth(clamp01((p - a) / (b - a)));

const ESpark = ({ size, tone }) => {
  const h = SPARK_H[size] || 16;
  const { cls, style } = toneProps(tone);
  return (
    <span className={cx("e-spark", cls)} style={style} aria-hidden="true">
      <svg viewBox="0 0 32 20" height={h} width={Math.round((h * 32) / 20)} fill="none">
        <polyline className="es-track" points="2,16 9,9 15,13 22,4 30,8" />
        <polyline className="es-lead" points="2,16 9,9 15,13 22,4 30,8" />
        <circle className="es-dot" r="1.8" cx="0" cy="0" />
      </svg>
    </span>
  );
};

// LoaderGlyph — the single source of the E-large chart geometry. Two modes:
//   · progress === undefined → LOOP: candles-only "market heartbeat" (wrapper
//     .dtl-mix drives a continuous breathing wave via CSS keyframes; no trend
//     line / dot / arrow — those are driven-only, see below).
//   · progress is a number (0..1) → PROGRESS-DRIVEN, no CSS loop: candles stagger
//     up (scaleY, bottom-anchored), the trend line draws (leading the candles), a
//     dot rides the drawn tip, and the breakout arrow pops in teal only once
//     progress reaches 1. Every value is a plain inline style computed from the
//     number (inline beats the .dtl-glyph CSS), so the parent just re-renders it
//     with an updated number as the pull moves (PullToRefresh feeds it from the
//     pull motion value via a subscription). tone="mono" keeps everything --fg-2
//     (the arrow stays teal — the sole colour).
export const LoaderGlyph = ({ size = "md", tone = "muted", progress, className }) => {
  const reduced = useReducedMotion();
  const driven = typeof progress === "number";
  const { h, w } = largeDims(size);

  if (!driven) {
    // Loop mode — candles-only "market heartbeat": just the candlesticks
    // breathing in a travelling wave (see .dtl-mix .ecandle CSS). No trend
    // line / dot / arrow / baseline — nothing but the candles, high-contrast
    // (--fg-1) so it reads clearly against either theme.
    const { cls, style } = toneProps(tone);
    return (
      <span className={cx("dtl-mix", cls, className)} style={style} aria-hidden="true">
        <svg viewBox="0 0 34 24" height={h} width={w} fill="none">
          <g className="ecandle e1"><rect className="ewick" x="4.55" y="13.5" width="0.9" height="9.5" rx="0.45" /><rect className="ebody" x="3" y="16" width="4" height="5" rx="0.8" /></g>
          <g className="ecandle e2"><rect className="ewick" x="10.55" y="8.5" width="0.9" height="10.5" rx="0.45" /><rect className="ebody" x="9" y="11" width="4" height="6" rx="0.8" /></g>
          <g className="ecandle e3"><rect className="ewick" x="16.55" y="11.5" width="0.9" height="9.5" rx="0.45" /><rect className="ebody" x="15" y="14" width="4" height="5" rx="0.8" /></g>
          <g className="ecandle e4"><rect className="ewick" x="22.55" y="4.5" width="0.9" height="11.5" rx="0.45" /><rect className="ebody" x="21" y="7" width="4" height="7" rx="0.8" /></g>
          <g className="ecandle e5"><rect className="ewick" x="28.55" y="7.5" width="0.9" height="10.5" rx="0.45" /><rect className="ebody" x="27" y="10" width="4" height="6" rx="0.8" /></g>
        </svg>
      </span>
    );
  }

  // Driven mode — candles-only, every scaleY a plain inline style computed from
  // `progress`. No trend line / dot / arrow: the candlesticks simply rise with
  // the pull. Once armed (progress ≥ 1) the whole glyph tints teal as the
  // "release to refresh" cue (the sole colour moment; the CSS transitions it).
  const v = clamp01(progress);
  const scales = reduced
    ? CANDLE_BANDS.map(() => (v > 0.35 ? 1 : 0)) // no per-candle build
    : CANDLE_BANDS.map((band) => bandScale(v, band));
  const armed = progress >= 1; // threshold reached → candles go teal

  // scaleY anchored bottom (transform-origin from .dtl-glyph .ecandle CSS).
  const candle = (i, wick, body) => (
    <g className={`ecandle e${i + 1}`} style={{ transform: `scaleY(${scales[i]})` }}>
      <rect className="ewick" {...wick} />
      <rect className="ebody" {...body} />
    </g>
  );

  const toneStyle = {
    ...(tone === "inherit" ? { color: "inherit" } : null),
    ...(armed ? { color: "var(--color-profit)" } : null),
  };

  return (
    <span className={cx("dtl-glyph", armed && "armed", className)} style={toneStyle} aria-hidden="true">
      <svg viewBox="0 0 34 24" height={h} width={w} fill="none">
        {candle(0, { x: "4.55", y: "13.5", width: "0.9", height: "9.5", rx: "0.45" }, { x: "3", y: "16", width: "4", height: "5", rx: "0.8" })}
        {candle(1, { x: "10.55", y: "8.5", width: "0.9", height: "10.5", rx: "0.45" }, { x: "9", y: "11", width: "4", height: "6", rx: "0.8" })}
        {candle(2, { x: "16.55", y: "11.5", width: "0.9", height: "9.5", rx: "0.45" }, { x: "15", y: "14", width: "4", height: "5", rx: "0.8" })}
        {candle(3, { x: "22.55", y: "4.5", width: "0.9", height: "11.5", rx: "0.45" }, { x: "21", y: "7", width: "4", height: "7", rx: "0.8" })}
        {candle(4, { x: "28.55", y: "7.5", width: "0.9", height: "10.5", rx: "0.45" }, { x: "27", y: "10", width: "4", height: "6", rx: "0.8" })}
      </svg>
    </span>
  );
};

export const Loader = ({ size = "md", tone = "muted", label, className, ...props }) => {
  const spark = size === "xs" || size === "sm";
  return (
    <div className={cx("dt-loader-wrap", className)} role="status" aria-label={label || "Loading"} {...props}>
      {spark ? <ESpark size={size} tone={tone} /> : <LoaderGlyph size={size} tone={tone} />}
      {label && <span className="dt-load-label">{label}</span>}
    </div>
  );
};

export default Loader;
